import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useT } from '../../i18n';
import { useCurrentUser } from '../../hooks/useAppData';
import { initials } from '../../lib/patient';
import { logOutEverywhere } from '../../services';

export function ProfileMenu() {
  const { role, logout } = useApp();
  const t = useT();
  const navigate = useNavigate();
  const { data: user } = useCurrentUser();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="profile-menu-button"
        aria-label={t('topbar.profile')}
        className="flex h-10 w-10 items-center justify-center rounded-full bg-sc-teal text-sm font-bold text-white ring-2 ring-white transition-transform duration-200 hover:scale-105"
      >
        {user ? initials(user.name) : '…'}
      </button>
      {open && user && (
        <div data-testid="profile-menu" className="sc-rise absolute right-0 top-12 z-40 w-64 rounded-2xl border border-sc-line bg-white p-4 shadow-lg">
          <p className="font-semibold text-slate-900" data-testid="profile-name">{user.name}</p>
          <p className="text-sm text-slate-500">{user.email}</p>
          <p className="mt-2 inline-block rounded-full bg-sc-soft px-2.5 py-1 text-xs font-semibold text-sc-teal" data-testid="profile-role">
            {t(`roles.${role}`)}
          </p>
          <button
            type="button"
            data-testid="sign-out-button"
            onClick={() => {
              logout();
              navigate('/login');
            }}
            className="mt-4 flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-sc-line text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            <LogOut className="h-4 w-4" />
            {t('topbar.signOut')}
          </button>
          <button
            type="button"
            data-testid="sign-out-everywhere-button"
            onClick={async () => {
              await logOutEverywhere().catch(() => undefined);
              logout();
              navigate('/login');
            }}
            className="mt-2 h-10 w-full text-xs font-semibold text-slate-500 underline"
          >
            {t('session.everywhere')}
          </button>
        </div>
      )}
    </div>
  );
}
